/**
 * Discount Popup
 * Handles show/dismiss logic with frequency control via localStorage.
 * Minimizes to a side tab when dismissed.
 */

if (!customElements.get('discount-popup')) {
  class DiscountPopup extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('.discount-popup-overlay');
      this.closeBtn = this.querySelector('.discount-popup__close');
      this.bgOverlay = this.querySelector('.discount-popup-overlay__bg');
      this.tab = this.querySelector('.discount-popup-tab');
      this.delay = parseInt(this.dataset.delay || '3', 10) * 1000;
      this.frequency = this.dataset.frequency || 'session';
      this.popupId = this.dataset.popupId || 'discount-popup';
      this.storageKey = `nb_popup_${this.popupId}`;
      this.isEditor = document.documentElement.classList.contains('shopify-design-mode');
    }

    connectedCallback() {
      if (!this.overlay) return;

      this.closeBtn?.addEventListener('click', () => this.hide());
      this.bgOverlay?.addEventListener('click', () => this.hide());
      this.tab?.addEventListener('click', () => this.show());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('active')) {
          this.hide();
        }
      });

      // In the theme editor, always show immediately
      if (this.isEditor) {
        this.show();
        return;
      }

      // Check if we should show based on frequency
      if (this.shouldShow()) {
        this.timer = setTimeout(() => this.show(), this.delay);
      } else {
        // Popup was already shown before — show the tab right away
        this.showTab();
      }
    }

    shouldShow() {
      switch (this.frequency) {
        case 'always':
          return true;

        case 'session': {
          return !sessionStorage.getItem(this.storageKey);
        }

        case 'day': {
          const lastShown = localStorage.getItem(this.storageKey);
          if (!lastShown) return true;
          const oneDayMs = 24 * 60 * 60 * 1000;
          return Date.now() - parseInt(lastShown, 10) > oneDayMs;
        }

        case 'once': {
          return !localStorage.getItem(this.storageKey);
        }

        default:
          return true;
      }
    }

    markShown() {
      switch (this.frequency) {
        case 'session':
          sessionStorage.setItem(this.storageKey, '1');
          break;
        case 'day':
        case 'once':
          localStorage.setItem(this.storageKey, Date.now().toString());
          break;
      }
    }

    show() {
      this.hideTab();
      this.overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.markShown();

      // Trap focus
      this.closeBtn?.focus();
    }

    hide() {
      this.overlay.classList.remove('active');
      document.body.style.overflow = '';

      if (this.timer) {
        clearTimeout(this.timer);
      }

      // Show the minimized tab after a brief delay
      setTimeout(() => this.showTab(), 300);
    }

    showTab() {
      this.tab?.classList.add('visible');
    }

    hideTab() {
      this.tab?.classList.remove('visible');
    }

    disconnectedCallback() {
      if (this.timer) {
        clearTimeout(this.timer);
      }
    }
  }

  customElements.define('discount-popup', DiscountPopup);
}

